import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, BarChart3, ChevronRight, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  buildExpenseDetailsKey,
  buildExpenseStatisticsKey,
  fetchPropertyExpenseDetails,
  fetchPropertyExpenseStatistics,
  selectPropertyExpenseDetailsBucket,
  selectPropertyExpenseStatisticsBucket,
} from '@/features/property-expenses';
import { cn, formatUnixDateTimeCompact } from '@/lib';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

type PropertyExpensesSummaryProps = {
  propertyId: string;
};

function formatMoney(value: number, currency?: string | null): string {
  if (currency) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  }

  return `$${Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatSignedMoney(value: number, currency?: string | null): string {
  if (currency) return formatMoney(value, currency);
  const prefix = value < 0 ? '-' : '';
  return `${prefix}${formatMoney(value, currency)}`;
}

function formatMonth(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;

  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
}

function formatRange(from?: number, to?: number): string {
  if (!from || !to) return 'Default ownership range';
  const start = new Date(from * 1000).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  const end = new Date(to * 1000).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  return `${start} - ${end}`;
}

function getPrimaryCurrency(periods: Array<{ currency?: string | null }>): string | null {
  return periods.find((period) => period.currency)?.currency ?? null;
}

export function PropertyExpensesSummary({ propertyId }: PropertyExpensesSummaryProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(null);

  const statisticsRequest = useMemo(() => ({ propertyID: propertyId }), [propertyId]);
  const statisticsKey = useMemo(() => buildExpenseStatisticsKey(statisticsRequest), [statisticsRequest]);
  const statisticsBucket = useAppSelector((state) => selectPropertyExpenseStatisticsBucket(state, statisticsKey));
  const statistics = statisticsBucket?.data;
  const periods = useMemo(() => statistics?.periods ?? [], [statistics?.periods]);
  const currency = getPrimaryCurrency(periods);
  const maxAbsPeriodSpend = Math.max(1, ...periods.map((period) => Math.abs(period.totalSpent)));
  const averageMonthlySpend = periods.length ? statistics!.totalSpent / periods.length : 0;

  const detailsRequest = useMemo(
    () =>
      selectedPeriodKey
        ? {
            propertyID: propertyId,
            query: { periodKey: selectedPeriodKey },
          }
        : null,
    [propertyId, selectedPeriodKey],
  );
  const detailsKey = useMemo(() => (detailsRequest ? buildExpenseDetailsKey(detailsRequest) : ''), [detailsRequest]);
  const detailsBucket = useAppSelector((state) =>
    detailsKey ? selectPropertyExpenseDetailsBucket(state, detailsKey) : undefined,
  );
  const details = detailsBucket?.data;
  const isStatisticsLoading =
    !statistics && !statisticsBucket?.error && (!statisticsBucket || statisticsBucket.loading);

  useEffect(() => {
    if (!propertyId) return;
    dispatch(fetchPropertyExpenseStatistics(statisticsRequest));
  }, [dispatch, propertyId, statisticsRequest]);

  useEffect(() => {
    if (!selectedPeriodKey) return;
    if (periods.some((period) => period.periodKey === selectedPeriodKey)) return;
    setSelectedPeriodKey(null);
  }, [periods, selectedPeriodKey]);

  useEffect(() => {
    if (!detailsRequest) return;
    dispatch(fetchPropertyExpenseDetails(detailsRequest));
  }, [detailsRequest, dispatch]);

  const refresh = () => {
    dispatch(fetchPropertyExpenseStatistics({ ...statisticsRequest, force: true }));
    if (detailsRequest) {
      dispatch(fetchPropertyExpenseDetails({ ...detailsRequest, force: true }));
    }
  };

  return (
    <Card className="h-full overflow-hidden">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 className="text-muted-foreground h-4 w-4" />
            <CardTitle className="text-base">Property expenditures</CardTitle>
            <Badge variant="outline">Monthly</Badge>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Project event rollups for the current ownership period.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={refresh} disabled={isStatisticsLoading}>
          <RefreshCw className={cn('mr-2 h-4 w-4', isStatisticsLoading ? 'animate-spin' : null)} />
          Refresh
        </Button>
      </CardHeader>

      <CardContent className="space-y-5">
        {statisticsBucket?.error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {statisticsBucket.error}
          </div>
        ) : null}

        {isStatisticsLoading ? (
          <div className="grid gap-4 md:grid-cols-[260px_1fr]">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        ) : null}

        {statistics ? (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-muted/25 rounded-md border p-4">
                <p className="text-muted-foreground text-xs">Total spent</p>
                <p className="mt-2 text-2xl font-semibold">{formatSignedMoney(statistics.totalSpent, currency)}</p>
                <p className="text-muted-foreground mt-1 text-xs">{formatRange(statistics.from, statistics.to)}</p>
              </div>
              <div className="bg-muted/25 rounded-md border p-4">
                <p className="text-muted-foreground text-xs">Monthly average</p>
                <p className="mt-2 text-2xl font-semibold">{formatSignedMoney(averageMonthlySpend, currency)}</p>
                <p className="text-muted-foreground mt-1 text-xs">{periods.length} monthly buckets</p>
              </div>
              <div className="bg-muted/25 rounded-md border p-4">
                <p className="text-muted-foreground text-xs">Basis</p>
                <p className="mt-2 text-sm font-medium">Expense rollup</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {statistics.sourceType ?? 'All sources'} · {statistics.periodType}
                </p>
              </div>
            </div>

            {periods.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">Monthly activity</p>
                  <p className="text-muted-foreground text-xs">Select a month to inspect the underlying events.</p>
                </div>

                <div className="grid min-h-44 grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-8">
                  {periods.map((period) => {
                    const isSelected = period.periodKey === selectedPeriodKey;
                    const isCredit = period.totalSpent < 0;
                    const height = Math.max(10, Math.round((Math.abs(period.totalSpent) / maxAbsPeriodSpend) * 112));

                    return (
                      <button
                        key={period.periodKey}
                        type="button"
                        onClick={() => setSelectedPeriodKey(period.periodKey)}
                        className={cn(
                          'group hover:border-ring/70 focus-visible:ring-ring/50 flex h-44 flex-col justify-end rounded-md border bg-transparent p-3 text-left transition focus-visible:ring-2 focus-visible:outline-none',
                          isSelected ? 'border-ring bg-muted/35' : 'border-border',
                        )}
                      >
                        <div className="flex flex-1 items-end">
                          <div
                            className={cn(
                              'w-full rounded-sm transition group-hover:opacity-90',
                              isCredit ? 'bg-chart-2' : 'bg-chart-1',
                            )}
                            style={{ height }}
                          />
                        </div>
                        <div className="mt-3">
                          <p className="text-xs font-medium">{formatMonth(period.periodKey)}</p>
                          <p className={cn('text-xs', isCredit ? 'text-emerald-400' : 'text-muted-foreground')}>
                            {formatSignedMoney(period.totalSpent, period.currency ?? currency)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="border-border bg-muted/20 rounded-md border p-4 text-sm">
                No project expenses have been recorded for this property yet.
              </div>
            )}

            {selectedPeriodKey ? (
              <>
                <Separator />

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{formatMonth(selectedPeriodKey)} details</p>
                      <p className="text-muted-foreground text-xs">
                        {details?.expenses.length ?? 0} expense events ·{' '}
                        {formatSignedMoney(details?.totalSpent ?? 0, currency)}
                      </p>
                    </div>
                  </div>

                  {detailsBucket?.error ? (
                    <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
                      {detailsBucket.error}
                    </div>
                  ) : null}

                  {!details && detailsBucket?.loading ? <Skeleton className="h-28" /> : null}

                  {details ? (
                    details.expenses.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Expense</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead className="w-8" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {details.expenses.map((expense) => {
                            const isCredit = expense.amount < 0;

                            return (
                              <TableRow
                                key={expense.id}
                                role="button"
                                tabIndex={0}
                                className="cursor-pointer"
                                onClick={() => {
                                  if (expense.routeTarget.entityType !== 'Project') return;
                                  navigate(
                                    `/properties/${encodeURIComponent(propertyId)}/projects/${encodeURIComponent(
                                      expense.routeTarget.entityID,
                                    )}`,
                                  );
                                }}
                                onKeyDown={(event) => {
                                  if (event.key !== 'Enter' && event.key !== ' ') return;
                                  event.preventDefault();
                                  event.currentTarget.click();
                                }}
                              >
                                <TableCell>
                                  <div className="flex items-start gap-2">
                                    {isCredit ? (
                                      <ArrowDownRight className="mt-0.5 h-4 w-4 text-emerald-400" />
                                    ) : (
                                      <ArrowUpRight className="text-muted-foreground mt-0.5 h-4 w-4" />
                                    )}
                                    <div>
                                      <p className="font-medium">{expense.title?.trim() || 'Untitled expense'}</p>
                                      <p className="text-muted-foreground text-xs">
                                        {expense.description?.trim() || expense.sourceID}
                                      </p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>{formatUnixDateTimeCompact(expense.occurredAt)}</TableCell>
                                <TableCell
                                  className={cn('text-right font-medium', isCredit ? 'text-emerald-400' : undefined)}
                                >
                                  {formatSignedMoney(expense.amount, expense.currency ?? currency)}
                                </TableCell>
                                <TableCell>
                                  <ChevronRight className="text-muted-foreground h-4 w-4" />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="border-border bg-muted/20 rounded-md border p-4 text-sm">
                        No expense events found for this month.
                      </div>
                    )
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
